import apiClient from '@/api/client'
import type { ReceiptReviewBlock } from '@/lib/printCustomerReceipt'
import {
  loadBrandLogo,
  renderBrandedQrDataUrl,
  toMonochromeDataUrl,
  QR_LOGO_SCALE_MIN,
} from '@/lib/brandedQr'

/**
 * Size the QR is rendered at when it carries a logo. The receipt renderer
 * draws it at RECEIPT_REVIEW_QR_PX in printCustomerReceipt.ts, which must
 * agree with this.
 */
const RECEIPT_QR_RENDER_PX = 320

export type ReceiptReviewBrandOptions = {
  /** `receipt_logo_url`, already validated by parseReceiptSettings. */
  logoUrl?: string
  /** Tenant-level `branded_qr_enabled`. */
  enabled?: boolean
}

/**
 * Fetch the per-order review invite and produce the receipt block.
 *
 * This is the path that reaches the guest nobody could reach before: the
 * walk-in who paid cash, left no phone number, and whose bad experience would
 * otherwise have surfaced first as a public one-star. The token on this QR
 * carries the order, so their rating lands attached to a real visit — server,
 * ticket, business day and all.
 *
 * Returns null when the tenant has the feature off, the guest already rated us
 * inside the cooldown, or the fetch fails. Errors are swallowed by design: a
 * marketing block must never stop a paid receipt from printing.
 *
 * Returns the single 'ask' block, or null. There is deliberately no text-only
 * variant and no "already rated" variant: a line telling a guest they already
 * rated us, or an ask with no QR they can scan, is ink spent on nothing.
 * Loyalty has a 'saved' variant only because it has progress worth reporting.
 */
export async function buildReceiptReviewBlock(
  orderId: string,
  brand?: ReceiptReviewBrandOptions,
): Promise<ReceiptReviewBlock | null> {
  try {
    const res = await apiClient.getOrderReviewInvite(orderId)
    if (!res.success || !res.data || !res.data.enabled) return null
    const { url, is_scannable, with_loyalty, headline, subline } = res.data

    const customHeadline = (headline || '').trim() || undefined
    const customSubline = (subline || '').trim() || undefined

    // No reachable origin (localhost / LAN dev, or a tenant whose public base
    // URL was never configured) — print nothing rather than an ask the guest
    // has no way to answer.
    if (!is_scannable || !url) return null

    // Thermal printers are 1-bit: the driver dithers anything between black
    // and white, and stray dots land in the modules the scanner is trying to
    // read. Collapse the logo to pure black/white first and use the smallest
    // allowed size — same treatment the loyalty QR gets, for the same reason.
    let logoDataUrl: string | null = null
    if (brand?.enabled !== false && brand?.logoUrl) {
      const raw = await loadBrandLogo(brand.logoUrl)
      if (raw) logoDataUrl = await toMonochromeDataUrl(raw)
    }

    const qrDataUri = await renderBrandedQrDataUrl({
      data: url,
      size: RECEIPT_QR_RENDER_PX,
      style: {
        dark_color: '#000000',
        light_color: '#ffffff',
        margin_modules: 1,
        dots_type: 'square',
        corner_square_type: 'square',
        corner_dot_type: 'square',
        error_correction: 'M',
      },
      logoDataUrl,
      logoEnabled: Boolean(logoDataUrl),
      logoScale: QR_LOGO_SCALE_MIN,
    })

    return {
      state: 'ask',
      url,
      qrDataUri,
      allowWithLoyalty: Boolean(with_loyalty),
      headline: customHeadline,
      subline: customSubline,
    }
  } catch {
    return null
  }
}
